import { useEffect, useState } from 'react'
import Link from 'next/link'
import { useRouter } from 'next/router'
import {
  Box,
  CircularProgress,
  Container,
  Skeleton,
  Typography,
} from '@mui/material'
import ComponentBanner from '../../components/ContentfulComponents/ComponentBanner'
import ComponentCTA from '../../components/ContentfulComponents/ComponentCTA'
import ComponentGrid from '../../components/ContentfulComponents/ComponentGrid'
import EmbedHtmlComponent from '../../components/ContentfulComponents/EmbedHtmlComponent'
import NextImageWrapper from '../../components/images/NextImageWrapper'
import CtaButton from '../../components/interactives/Buttons/CtaButton'
import Layout from '../../components/layout'
import GreyTwoColumnFooter from '../../components/layouts/page/GreyTwoColumnFooter'
import PageContent from '../../components/PageContentComponents/PageContent'
import WriteForUsContent from '../../components/PageContentComponents/WriteForUsContent'
import SEOHead from '../../components/SEOHead'
import TwoColumnCta from '../../components/TwoColumnCta'
import {
  // hasTermResourceKeyword,
  // PTN,
  hasAccessToPageOrWebinar,
  hasASCDMemberBadge,
} from '../../lib/access-validator'
import { getContentfulDirectClient } from '../../lib/apollo-client'
import { contentfulAPIToSEOHead } from '../../lib/data-transformations'
import useUserAccount from '../../lib/hooks/useUserAccount'
import GET_COMPONENT_BANNER from '../../lib/schema/getComponentBanner.graphql'
import GET_COMPONENT_GRID from '../../lib/schema/getComponentGrid.graphql'
import GET_COMPONENT_CTA from '../../lib/schema/getCta.graphql'
import GET_EMBED_HTML from '../../lib/schema/getEmbedHtmlComponent.graphql'
import GET_PAGE_BY_SLUG_QUERY_FIVE from '../../lib/schema/pages/getPageBySlugFive.graphql'
import GET_PAGE_BY_SLUG_QUERY_FOUR from '../../lib/schema/pages/getPageBySlugFour.graphql'
import GET_PAGE_BY_SLUG_QUERY_ONE from '../../lib/schema/pages/getPageBySlugOne.graphql'
import GET_PAGE_BY_SLUG_QUERY_THREE from '../../lib/schema/pages/getPageBySlugThree.graphql'
import GET_PAGE_BY_SLUG_QUERY_TWO from '../../lib/schema/pages/getPageBySlugTwo.graphql'
import GET_PAGE_LIST_QUERY from '../../lib/schema/pages/getPageList.graphql'
import { reconcilePageData } from '../../lib/utils'

export default function Page({
  slug,
  content,
  seo,
  pageId,
  memberAuthentication,
  buttonLink,
  banner,
  grid,
  ctaComponent,
  embedHtmlComponent,
}) {
  const router = useRouter()
  const { user, userAccountUser, userAccountAccess } = useUserAccount()
  const [hasAccess, setHasAccess] = useState(false)
  const [loading, setLoading] = useState(true)

  const url = '#'
  const handleLoginClick = () => {
    const returnUrl = window.location.href
    window.location.href = `${process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_URL}?returnUrl=${returnUrl}`
    sessionStorage.setItem('returnUrl', returnUrl)
  }
  const membership = (memberauthentication) => {
    if (memberauthentication?.length) {
      return memberauthentication?.split(' ')[0]
    }
  }
  const hasPageOrWebinarAccess = hasAccessToPageOrWebinar(
    membership(memberAuthentication),
    userAccountAccess
  )

  useEffect(() => {
    const checkAccess = async () => {
      setLoading(true)

      if (user?.authenticated && pageId === 'ASCD Central') {
        const userHasAccess = async () => {
          // const hasAccess = await hasTermResourceKeyword(
          //   [
          //     PTN.SELECT_SUBSCRIPTION_KEYWORD,
          //     PTN.PREMIUM_SUBSCRIPTION_KEYWORD,
          //     PTN.DIGITAL_BASIC_ANNUAL,
          //     PTN.STANDARD_SELECT_MONTHLY,
          //     PTN.PAID_SUBSCRIPTIONS_KEYWORDS,
          //     PTN.PAYWALL_MEMBERSHIP_KEYWORDS,
          //     PTN.ELBADGE_KEYWORDS,
          //     PTN.MEMBER_BADGE_KEYWORDS,
          //   ],
          //   userAccountAccess
          // )
          const hasAccess = await hasASCDMemberBadge(
            userAccountUser?.salesforceAccess?.user?.badges
          )

          setHasAccess(hasAccess)
        }

        await userHasAccess()
      }
    }

    checkAccess()
  }, [user, user?.authenticated, userAccountAccess, pageId, hasAccess])

  useEffect(() => {
    setTimeout(() => {
      setLoading(false)
    }, 6000)
  }, [user?.authenticated, hasAccess, pageId])

  useEffect(() => {
    const slug = router.asPath?.split('#').pop()
    if (slug) {
      const checkExist = setInterval(function () {
        const el = document.getElementById(slug)
        if (el) {
          navigateTo(el)
          clearInterval(checkExist)
        }
      }, 3000)
    }
  }, [router.asPath])

  const navigateTo = (el) => {
    const r = el.getBoundingClientRect()
    window.scrollTo({ top: scrollY + r.top - 130, behavior: 'smooth' })
  }

  if (!content.items.length) return null

  if (loading && pageId === 'ASCD Central') {
    return (
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          alignItems: 'center',
        }}
      >
        <NextImageWrapper
          testId='ascd-logo-desktop'
          priority
          src={'/images/logo.svg'}
          alt='ascd logo'
          width={109}
          height={36}
        />
        <CircularProgress />
      </Box>
    )
  }

  // if (!user?.authenticated && pageId === 'ASCD Central') {
  //   if (typeof window !== 'undefined') {
  //     const returnUrl = window.location.href
  //     router.push(
  //       `${process.env.NEXT_PUBLIC_SALESFORCE_BACKEND_URL}?returnUrl=${returnUrl}`
  //     )
  //     sessionStorage.setItem('returnUrl', returnUrl)
  //   }
  //   return null
  // }

  return (
    <Layout grey={slug === 'activate' || slug === 'services'}>
      <SEOHead seo={seo} />
      {user?.authenticated && hasAccess && pageId === 'ASCD Central' && (
        <Container maxWidth='lg'>
          {banner && (
            <ComponentBanner
              ctaItems={banner?.cta?.items}
              image={banner?.image}
              title={
                user?.authenticated
                  ? `Welcome ${userAccountUser?.salesforceAccess?.user?.firstName}, to the new ASCD member benefits webpage!`
                  : 'Welcome to the new ASCD member benefits webpage!'
              }
              subtitle={user?.authenticated ? banner.subtitle : ''}
              titleAboveImage={banner.titleAboveImage}
              titleCenterAlign={banner.titleCenterAlign}
              bannerBody={banner.bannerBody}
              imagePlacement={banner.imagePlacement}
              imageBorderCornerPosition={banner.imageBorderCornerPosition}
              imageMobilePosition={banner.imageMobilePosition}
              backgroundColor={banner.backgroundColor}
            />
          )}
          {embedHtmlComponent && (
            <EmbedHtmlComponent
              displayTitle={embedHtmlComponent.displayTitle}
              displayTitleTextAlignment={
                embedHtmlComponent.displayTitleTextAlignment
              }
              body={embedHtmlComponent.body}
              bodyTextAlignment={embedHtmlComponent.bodyTextAlignment}
              htmlCodeType={embedHtmlComponent.htmlCodeType}
              htmlCode={embedHtmlComponent.htmlCode}
              displayFormat={embedHtmlComponent.displayFormat}
              backgroundColor={embedHtmlComponent.backgroundColor}
            />
          )}
          {grid && (
            <ComponentGrid
              testId={'grid-component'}
              gridItems={grid?.gridItems?.items}
              title={grid.title}
              gridBody={grid}
              bodyCentered={grid.bodyCentered}
              layout={grid.layout}
            />
          )}
          {ctaComponent && (
            <ComponentCTA
              testId={'component-cta'}
              ctaItems={ctaComponent?.ctaItems}
              title={ctaComponent.title}
              titleAlignment={ctaComponent.titleAlignment}
              ctaBody={ctaComponent.ctaBody}
              bodyAlignment={ctaComponent.bodyAlignment}
              ctaLink={ctaComponent.ctaLink}
              backgroundColor={ctaComponent.backgroundColor}
              backgroundImage={ctaComponent.backgroundImage}
              roundedModule={ctaComponent.roundedModule}
            />
          )}
        </Container>
      )}
      {user?.authenticated && pageId === 'ASCD Central' && !hasAccess && (
        <Container maxWidth='lg'>
          <TwoColumnCta
            testId='404-2-col-cta'
            ctaItems={[
              {
                __typename: 'ButtonLinkComponent',
                linkUrl: '/membership-details',
                linkLabel: 'Not a member? Join now.',
                buttonStyle: ['Text Link w/Arrow (Preset #2)'],
              },
              {
                __typename: 'ButtonLinkComponent',
                linkUrl: '/contact',
                linkLabel: 'Have questions? Contact ASCD',
                buttonStyle: ['Text Link w/Arrow (Preset #2)'],
              },
            ]}
            description='Welcome to the new member benefits page!'
            image='/images/image404.png'
            imagePos='right'
            variant='error'
            descriptionLineNumbers={8}
          />
        </Container>
      )}
      {!user?.authenticated && pageId === 'ASCD Central' && (
        <Container
          maxWidth='lg'
          sx={{
            marginTop: 4,
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
          }}
        >
          <Container maxWidth='lg'>
            {banner && (
              <ComponentBanner
                ctaItems={banner?.cta?.items}
                image={banner?.image}
                title={'Welcome to the new ASCD member benefits page!'}
                subtitle={user?.authenticated ? banner.subtitle : ''}
                titleAboveImage={banner.titleAboveImage}
                titleCenterAlign={banner.titleCenterAlign}
                bannerBody={banner.bannerBody}
                imagePlacement={banner.imagePlacement}
                imageBorderCornerPosition={banner.imageBorderCornerPosition}
                imageMobilePosition={banner.imageMobilePosition}
                backgroundColor={banner.backgroundColor}
              />
            )}
            <Skeleton variant='rectangular' height={200} />
            <Skeleton variant='rectangular' height={200} />
            <Skeleton variant='rectangular' height={200} />
          </Container>
          <Box
            sx={{
              position: 'fixed',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              bgcolor: 'rgba(0, 0, 0, 0.75)',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              zIndex: 1000,
            }}
          >
            <Box
              sx={{
                width: 400,
                bgcolor: 'white',
                boxShadow: 24,
                padding: 4,
                display: 'flex',
                alignItems: 'center',
                flexDirection: 'column',
                gap: 2,
                zIndex: 1001,
              }}
            >
              <Typography variant='h6' component='h2' gutterBottom>
                Login Required
              </Typography>
              <Typography variant='body2'>
                Please log in to access your ASCD Member Benefits
              </Typography>
              <CtaButton
                testId='login-button'
                variant='outlined'
                color='primary'
                label='Log In'
                onClick={handleLoginClick}
              />
            </Box>
          </Box>
        </Container>
      )}
      <Box mt={[0, 0, 5]} mb={[6, 10]} data-testid='page-content'>
        {(!memberAuthentication ||
          memberAuthentication === 'None' ||
          (memberAuthentication === 'Registered User' && user?.authenticated) ||
          ((memberAuthentication === 'Basic Member' ||
            memberAuthentication === 'Select Member' ||
            memberAuthentication === 'Premium Member') &&
            user?.authenticated &&
            hasPageOrWebinarAccess)) && (
          <PageContent content={content} pageId={pageId} />
        )}
        {(memberAuthentication === 'Registered User' ||
          memberAuthentication === 'Basic Member' ||
          memberAuthentication === 'Select Member' ||
          memberAuthentication === 'Premium Member') &&
          !user?.authenticated && (
            <Container maxWidth='lg'>
              <Box mt={[15, 20]}>
                <Typography>
                  This content is a benefit of one or more of our membership
                  plans. Please{' '}
                  <a
                    href={url}
                    onClick={handleLoginClick}
                    style={{ textDecoration: 'underline' }}
                  >
                    log in
                  </a>{' '}
                  to your account to view it, or visit our{' '}
                  <Link href='/memberships'>
                    <a style={{ textDecoration: 'underline' }}>
                      membership page
                    </a>
                  </Link>{' '}
                  to explore your options.
                </Typography>
              </Box>
            </Container>
          )}
        {(memberAuthentication === 'Basic Member' ||
          memberAuthentication === 'Select Member' ||
          memberAuthentication === 'Premium Member') &&
          user?.authenticated &&
          !hasPageOrWebinarAccess && (
            <Container maxWidth='lg'>
              <Box mt={[15, 20]}>
                <Typography>
                  You do not have access to this content. Please{' '}
                  <Link href='/contact'>
                    <a style={{ textDecoration: 'underline' }}>contact us</a>
                  </Link>{' '}
                  for assistance if you believe this to be incorrect, or visit
                  our{' '}
                  <Link href='/memberships'>
                    <a style={{ textDecoration: 'underline' }}>
                      membership page
                    </a>
                  </Link>{' '}
                  to explore your options.
                </Typography>
              </Box>
            </Container>
          )}
      </Box>
      {slug === 'write-for-ascd' && (
        <Container data-testid='write-for-us-content'>
          <WriteForUsContent />
        </Container>
      )}
      {slug !== 'activate' && (
        <GreyTwoColumnFooter variant={slug} buttonLink={buttonLink} />
      )}
    </Layout>
  )
}

export async function getStaticPaths() {
  const contentfulDirectClient = await getContentfulDirectClient()
  const {
    data: { pages: pageData },
  } = await contentfulDirectClient.query({
    query: GET_PAGE_LIST_QUERY,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: Number(process.env.NEXT_STATIC_BUILD_LIMIT || 200),
    },
  })

  const paths = pageData?.items
    ?.filter(
      (item) => item?.slug && !['workshops', 'blogs'].includes(item?.slug)
    )
    .map((item) => ({
      params: { page: item.slug.split('/') },
    }))

  return {
    paths: paths,
    fallback: 'blocking',
  }
}

export async function getStaticProps({ params }) {
  /** The page query as a whole is too large for Contentful limits (8kb query size), so
   * it is being broken into 3 parts and reconciled after fetching
   */
  const contentfulDirectClient = await getContentfulDirectClient()
  const {
    data: {
      pages: { items },
    },
  } = await contentfulDirectClient.query({
    query: GET_PAGE_BY_SLUG_QUERY_ONE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.page.join('/') },
    },
  })

  const {
    data: {
      pages: { items: itemsTwo },
    },
  } = await contentfulDirectClient.query({
    query: GET_PAGE_BY_SLUG_QUERY_TWO,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.page.join('/') },
    },
  })

  const {
    data: {
      pages: { items: itemsThree },
    },
  } = await contentfulDirectClient.query({
    query: GET_PAGE_BY_SLUG_QUERY_THREE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      where: { slug: params.page.join('/') },
    },
  })

  const {
    data: {
      pages: { items: itemsFive },
    },
  } = await contentfulDirectClient.query({
    query: GET_PAGE_BY_SLUG_QUERY_FIVE,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      limit: 1,
      memberPriceCollectionLimit: 6,
      memberPriceItemCollectionLimit: 10,
      where: { slug: params.page.join('/') },
    },
  })

  /** rich text component is 'too complex' for single query here */
  let promiseChain = []

  for (let i = 0; i < 5; i++) {
    promiseChain.push([
      await contentfulDirectClient.query({
        query: GET_PAGE_BY_SLUG_QUERY_FOUR,
        variables: {
          preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
          skip: 10 * i,
          limit: 1,
          contentCollectionLimit: 10,
          where: { slug: params.page.join('/') },
        },
      }),
    ])
  }

  const itemsData = await Promise.all(promiseChain)

  const restItems = [...itemsData.flat()]
    .map((items) => items?.data?.pages?.items?.[0]?.content?.items)
    .flat()

  const itemsFour = [{ content: { items: [...restItems] } }]

  const page = reconcilePageData(
    items,
    itemsTwo,
    itemsThree,
    itemsFour,
    itemsFive
  )

  const {
    data: { banner },
  } = await contentfulDirectClient.query({
    query: GET_COMPONENT_BANNER,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      id: '7IujXoobErOPtRGHejew2R',
    },
  })

  const {
    data: { grid },
  } = await contentfulDirectClient.query({
    query: GET_COMPONENT_GRID,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      id: '6hYceY2xpPbzXCtKTMVG76',
    },
  })

  const {
    data: { cta: ctaComponent },
  } = await contentfulDirectClient.query({
    query: GET_COMPONENT_CTA,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      id: '5ztHLZobCgoZscQPmLYOYy',
    },
  })

  const {
    data: { embedHtmlComponent },
  } = await contentfulDirectClient.query({
    query: GET_EMBED_HTML,
    variables: {
      preview: process.env.NEXT_PUBLIC_CONTENTFUL_PREVIEW === 'true',
      id: '3nXhIAKo6tcKEjpHFWSPDV',
    },
  })

  if (!page || !page?.content?.items || page?.content?.items?.length === 0) {
    return {
      notFound: true,
    }
  }

  const {
    slug,
    content,
    seo,
    pageId,
    memberAuthentication,
    buttonLink,
    title,
    summary,
    sys,
    __typename,
  } = page

  return {
    props: {
      slug,
      content,
      seo:
        seo ||
        contentfulAPIToSEOHead({
          title,
          summary: summary?.json,
          sys,
          __typename,
        }),
      pageId,
      memberAuthentication,
      buttonLink,
      banner: banner || null,
      grid: grid || null,
      ctaComponent: ctaComponent || null,
      embedHtmlComponent: embedHtmlComponent || null,
    },
    revalidate: 20,
  }
}
